import { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FaCheck } from 'react-icons/fa';

export const THEMES = [
  // Light
  { id: 'slate',    label: 'Slate',    accent: '#6366f1', bg: '#f8fafc', isDark: false },
  { id: 'arctic',   label: 'Arctic',   accent: '#1565C0', bg: '#EEF4FF', isDark: false },
  { id: 'emerald',  label: 'Emerald',  accent: '#059669', bg: '#f0fdf9', isDark: false },
  // Dark
  { id: 'midnight', label: 'Midnight', accent: '#818cf8', bg: '#0f172a', isDark: true  },
  { id: 'noir',     label: 'Noir',     accent: '#E5C76B', bg: '#111111', isDark: true  },
  { id: 'terminal', label: 'Terminal', accent: '#4ade80', bg: '#030712', isDark: true  },
];

export function applyTheme(themeId) {
  const theme = THEMES.find((t) => t.id === themeId) ?? THEMES[0];
  document.documentElement.setAttribute('data-theme', theme.id);
  document.documentElement.setAttribute('data-bs-theme', theme.isDark ? 'dark' : 'light');
  localStorage.setItem('rv-theme', theme.id);
}

const ThemeToggle = () => {
  const [current, setCurrent] = useState('slate');

  useEffect(() => {
    const saved = localStorage.getItem('rv-theme') ?? 'slate';
    setCurrent(saved);
    applyTheme(saved);
  }, []);

  const handleSelect = (themeId) => {
    setCurrent(themeId);
    applyTheme(themeId);
  };

  const active = THEMES.find((t) => t.id === current) ?? THEMES[0];
  const lightThemes = THEMES.filter((t) => !t.isDark);
  const darkThemes  = THEMES.filter((t) =>  t.isDark);

  return (
    <Dropdown align={{ lg: 'end' }}>
      <Dropdown.Toggle
        variant="link"
        bsPrefix="btn"
        className="d-flex align-items-center gap-2 btn btn-sm"
        style={{
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: 8,
          padding: '4px 10px',
          color: 'var(--navbar-color, #fff)',
          textDecoration: 'none',
        }}
        title="Change theme"
      >
        {/* Live swatch of current theme */}
        <span
          style={{
            display: 'inline-block',
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: `radial-gradient(circle at 35% 35%, ${active.accent}, ${active.bg})`,
            border: '2px solid rgba(255,255,255,0.35)',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{active.label}</span>
      </Dropdown.Toggle>

      <Dropdown.Menu
        style={{ minWidth: 170, padding: '0.5rem 0' }}
        popperConfig={{
          modifiers: [
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport', padding: 8 },
            },
            {
              name: 'flip',
              options: { fallbackPlacements: ['top-end', 'top-start', 'bottom-start'] },
            },
          ],
        }}
      >
        <Dropdown.Header style={{ fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0.25rem 1rem' }}>
          Light
        </Dropdown.Header>
        {lightThemes.map((t) => (
          <ThemeItem key={t.id} theme={t} active={current === t.id} onSelect={handleSelect} />
        ))}

        <Dropdown.Divider />

        <Dropdown.Header style={{ fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0.25rem 1rem' }}>
          Dark
        </Dropdown.Header>
        {darkThemes.map((t) => (
          <ThemeItem key={t.id} theme={t} active={current === t.id} onSelect={handleSelect} />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const ThemeItem = ({ theme, active, onSelect }) => (
  <Dropdown.Item
    onClick={() => onSelect(theme.id)}
    className="d-flex align-items-center gap-2"
    style={{ fontSize: '0.875rem', padding: '0.4rem 1rem' }}
  >
    <span
      style={{
        display: 'inline-block',
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: `radial-gradient(circle at 35% 35%, ${theme.accent}, ${theme.bg})`,
        border: '1.5px solid rgba(128,128,128,0.3)',
        flexShrink: 0,
      }}
    />
    <span style={{ flex: 1 }}>{theme.label}</span>
    {active && <FaCheck size={11} style={{ opacity: 0.7 }} />}
  </Dropdown.Item>
);

export default ThemeToggle;
